/**
 * The quick-add button's two derived strings, written once for both tracks.
 *
 * `ProductCard` (the mobile card) and `DesktopListingCard` (the desktop one)
 * render the SAME control with the same three states, so the rule that picks
 * its words lives here rather than being typed twice and drifting. It is a
 * plain module with no imports precisely so `ProductCard` can use it: the
 * dependency runs `components/mobile/organisms` -> `patterns/market`, the same
 * direction as the `market.css` import already beside it, and reaching for
 * `patterns/market/index.jsx` instead would close a cycle (that file imports
 * `ProductCard`).
 */

/**
 * WHAT THE BUTTON SAYS. Pending wins over in-cart, and that ordering is
 * deliberate: a buyer who already has one in the cart and taps for a second
 * copy is mid-request, and "In cart" would describe the state they are trying
 * to change rather than the one they are waiting on.
 */
export function quickAddText({ pending, pendingLabel, inCart, label }) {
  if (pending) return pendingLabel;
  return inCart ? 'In cart' : label;
}

/**
 * WHAT ASSISTIVE TECH HEARS, or `undefined` to leave the visible text as the
 * accessible name.
 *
 * A buyer moving between controls never reaches the price row above the
 * button, so a tile whose figure is only a qualified guess has to say so on
 * the control itself -- "the tile says so before the tap" is not satisfied by
 * text a keyboard or screen-reader user only meets after tapping.
 *
 * The visible text stays a PREFIX of the accessible name (WCAG 2.5.3, Label in
 * Name), so voice control still activates the button by the words on it.
 *
 * WHILE PENDING, NO NOTE. The button is already saying the one thing that
 * matters -- that the very refresh which will settle the note is running --
 * and "Confirming price… — Price unconfirmed" reads as a contradiction rather
 * than as two facts.
 */
export function quickAddAccessibleName({ text, note, pending }) {
  if (!note || pending) return undefined;
  return `${text} — ${note}`;
}
